/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React from 'react';
import { css, StyleSheet } from 'aphrodite';
import { BrandColors, PoweredByBlendSVG, LoneIconButton, Text } from '@blend/enterprise-components';

import { pageSize } from './constants';

const styles = StyleSheet.create({
  footerWrapper: {
    backgroundColor: BrandColors.BACKGROUND_GREY,
    borderTop: `1px solid ${BrandColors.CLOUD_50}`,
    height: 65,
    width: '100%',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
  },
  blendWrapper: {
    alignItems: 'center',
    display: 'flex',
    height: 65,
    left: 'calc(50% - 52.5px)', // logo is 105px wide
    position: 'absolute',
  },
  paginationWrapper: {
    alignItems: 'center',
    display: 'flex',
    padding: '16px 20px',
    position: 'absolute',
    right: 0,
  },
  numRowsWrapper: {
    paddingRight: 20,
  },
  buttonWrapper: {
    display: 'inline-block',
    padding: '0 4px',
  },
  pageNumberContainer: {
    margin: '0 12px',
    textAlign: 'center',
    width: 56,
  },
});

export namespace Footer {
  export interface Props {
    page: number;
    totalCount: number;
    onPageChange: (page: number) => void;
  }
}

export const Footer: React.FC<Footer.Props> = ({ page, totalCount, onPageChange }) => {
  const numRows = Math.min(totalCount, pageSize);
  const firstRow = numRows ? numRows * page + 1 : 0; // if there's 0 rows, show 0
  const lastRow = Math.min(numRows * (page + 1), totalCount);
  const hasNext = (page + 1) * pageSize < totalCount;
  const hasPrevious = page > 0;
  const lastPage = Math.floor(totalCount / pageSize);

  return (
    <div className={css(styles.footerWrapper)} data-track-context="Footer">
      <div className={css(styles.blendWrapper)}>
        <a target="_blank" title="Blend" href="https://blend.com">
          <PoweredByBlendSVG />
        </a>
      </div>
      <div className={css(styles.paginationWrapper)}>
        <span id="it-num-rows-wrapper" className={css(styles.numRowsWrapper)}>
          <Text>
            {firstRow}-{lastRow} of {totalCount}
          </Text>
        </span>
        <div className={css(styles.buttonWrapper)}>
          <LoneIconButton
            icon="first_page"
            id="it-page-first"
            btnSize="sm"
            theme="tertiary"
            onClick={() => {
              onPageChange(0);
            }}
            disabled={!hasPrevious}
          />
        </div>
        <div className={css(styles.buttonWrapper)}>
          <LoneIconButton
            icon="chevron_left"
            id="it-page-back"
            btnSize="sm"
            theme="tertiary"
            onClick={() => {
              onPageChange(Math.max(0, page - 1));
            }}
            disabled={!hasPrevious}
          />
        </div>
        <span id="it-page-number-container" className={css(styles.pageNumberContainer)}>
          <Text>Page {page + 1}</Text>
        </span>
        <div className={css(styles.buttonWrapper)}>
          <LoneIconButton
            icon="chevron_right"
            id="it-page-forward"
            btnSize="sm"
            theme="tertiary"
            onClick={() => {
              onPageChange(page + 1);
            }}
            disabled={!hasNext}
          />
        </div>
        <div className={css(styles.buttonWrapper)}>
          <LoneIconButton
            icon="last_page"
            id="it-page-last"
            btnSize="sm"
            theme="tertiary"
            onClick={() => {
              onPageChange(lastPage);
            }}
            disabled={!hasNext}
          />
        </div>
      </div>
    </div>
  );
};
